import {
	ActionButton,
	FilterChip,
	ListPageChartCard,
	ListPageCharts,
	ListPageLayout,
	Pagination,
	SectionCard,
} from '@practium/ui';
import type { SectionTab } from '@practium/ui';
import { UsersPageSkeleton } from '../components/Skeletons/UsersPageSkeleton';
import { PiePercentageChart, ComparisonLineChart } from '@practium/features/dashboard';
import { UsersTable } from '../components/Tables/UsersTable/UsersTable';
import type { UseUsersPageProps } from '../hooks/useUsersPage';
import { useUsersPage } from '../hooks/useUsersPage';

export interface UsersPageProps extends UseUsersPageProps {
	onAddNew?: () => void;
	onViewItem?: (id: string) => void;
	onReportClick?: () => void;
}

export const UsersPage = ({
	onAddNew,
	onViewItem,
	onReportClick,
	...hookProps
}: UsersPageProps) => {
	const {
		filteredUsers,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort,
		filterValues,
		setFilterValues,
		filterGroups,
		page,
		setPage,
		totalPages,
		isLoading,
		permissions,
		profilesPieData,
		insertionsLineData,
		chartMainValue,
		chartTrendValue,
		chartTrendDirection,
		insertionsFooterText,
	} = useUsersPage(hookProps);

	if (isLoading) return <UsersPageSkeleton />;

	const handleReport = () => onReportClick?.();

	const tabs: SectionTab<'users'>[] = [
		{
			key: 'users',
			label: 'Usuários',
			content: <UsersTable users={filteredUsers} onRowClick={onViewItem} />,
		},
	];

	return (
		<ListPageLayout
			sidebarWidth="27rem"
			sidebarW1500="22rem"
			sidebarW1300="19rem"
			sidebarW1200="17rem"
			sidebar={
				<ListPageCharts>
					<ListPageChartCard>
						<PiePercentageChart
							title="Distribuição de Perfis"
							subtitle="Distribuição de pessoas na plataforma"
							buttonText="Ver Relatório"
							onButtonClick={handleReport}
							data={profilesPieData}
						/>
					</ListPageChartCard>
					<ListPageChartCard>
						<ComparisonLineChart
							title="Inserções na Plataforma"
							mainValue={chartMainValue}
							trendValue={chartTrendValue}
							trendLabel="vs semana anterior"
							trendDirection={chartTrendDirection}
							footerText={insertionsFooterText}
							buttonText="Ver Relatório"
							onButtonClick={handleReport}
							data={insertionsLineData}
							xAxisKey="day"
							line1Key="atual"
							line1Label="Semana atual"
							line2Key="anterior"
							line2Label="Semana anterior"
							tooltipTitle="Inserções"
						/>
					</ListPageChartCard>
				</ListPageCharts>
			}
		>
			<SectionCard
				title="Gestão de Usuários"
				subtitle="Gerencie os usuários cadastrados no sistema"
				tabs={tabs}
				activeTab="users"
				onTabChange={() => {}}
				search={true}
				searchQuery={searchQuery}
				onSearch={setSearchQuery}
				sortAsc={sortAsc}
				onToggleSort={toggleSort}
				filterSlot={
					<FilterChip
						values={filterValues}
						onChange={setFilterValues}
						groups={filterGroups}
						placeholder="Filtros"
					/>
				}
				footer={<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
				headerAction={
					<>
						{permissions.canCreate && (
							<ActionButton text="Adicionar" onClick={() => onAddNew?.()} />
						)}
						<ActionButton text="Ver Relatório" onClick={handleReport} />
					</>
				}
			/>
		</ListPageLayout>
	);
};
